// Packages.jsx
import React, { useEffect, useState, useCallback, useRef } from "react";
import useContract from "../hooks/useContract";
import {
  useAppKitAccount,
  useAppKit,
} from "@reown/appkit/react";
import { Contract, formatUnits, JsonRpcProvider } from "ethers";
import { useLocation } from "react-router-dom";
import {
  KIND_NETWORK_REWARD_CONTRACT_ADDRESS,
  TOKEN_CONTRACT_ADDRESS,
  REWARD_NETWORK_KIND_ABI,
} from "../contracts/contracts";
import { toast } from "react-hot-toast";

const ZERO = "0x0000000000000000000000000000000000000000";
const RPC_URL = "https://bsc-dataseed.binance.org";

const KIND_TOKEN_ABI = [
  "function balanceOf(address) view returns (uint256)",
  "function symbol() view returns (string)",
  "function decimals() view returns (uint8)",
];

const Packages = () => {
  const { address, isConnected } = useAppKitAccount();
  const { open } = useAppKit();
  const location = useLocation();

  const {
    getUserPackages,
    buyPackage,
    claimReward,
    terminate,
    terminateShare,
    getProvider,
  } = useContract();

  const [packages, setPackages] = useState([]);
  const [myPackages, setMyPackages] = useState([]);
  const [tab, setTab] = useState("my");
  const [loading, setLoading] = useState(true); // ✅ only initial load

  const [buyLoading, setBuyLoading] = useState({});
  const [claimLoading, setClaimLoading] = useState({});
  const [terminateAllLoading, setTerminateAllLoading] = useState({});
  const [terminateSingleLoading, setTerminateSingleLoading] = useState({});

  const [totalActive, setTotalActive] = useState(0);
  const [kindPrice, setKindPrice] = useState(0);
  const [kindBalance, setKindBalance] = useState("0");

  const KIND_ADDRESS = "0x41f52A42091A6B2146561bF05b722Ad1d0e46f8b";
  const KIND_BNB_PAIR = "0x628C0fe6DA9854EDf0f3E2C7657af6ecf29C740E";

  const provider = new JsonRpcProvider(RPC_URL);
  const contract = new Contract(KIND_NETWORK_REWARD_CONTRACT_ADDRESS, REWARD_NETWORK_KIND_ABI, provider);

  const isRefreshingRef = useRef(false);

  // ✅ MEMOIZED FETCH FUNCTIONS
  const fetchKindBalance = useCallback(async () => {
    if (!address) {
      setKindBalance("0");
      return;
    }
    try {
      const kindContract = new Contract(TOKEN_CONTRACT_ADDRESS, KIND_TOKEN_ABI, provider);
      const balance = await kindContract.balanceOf(address);
      const formattedBalance = formatUnits(balance, 18);
      setKindBalance(formattedBalance);
      console.log("🔄 REFRESH - KIND Balance:", formattedBalance);
    } catch (err) {
      console.log("Balance fetch error:", err);
      setKindBalance("0");
    }
  }, [address]);

  const fetchKindPrice = useCallback(async () => {
    try {
      const pair = new Contract(KIND_BNB_PAIR, [
        "function getReserves() view returns (uint112,uint112,uint32)",
        "function token0() view returns (address)",
      ], provider);

      const reserves = await pair.getReserves();
      const token0 = await pair.token0();

      let kindReserve, bnbReserve;
      if (token0.toLowerCase() === KIND_ADDRESS.toLowerCase()) {
        kindReserve = reserves[0];
        bnbReserve = reserves[1];
      } else {
        kindReserve = reserves[1];
        bnbReserve = reserves[0];
      }

      const kindPerBNB =
        parseFloat(formatUnits(bnbReserve, 18)) / parseFloat(formatUnits(kindReserve, 18));

      const feed = new Contract(
        "0x0567F2323251f0Aab15c8dFb1967E4e8A7D42aeE",
        [
          "function latestRoundData() view returns (uint80,int256,uint256,uint256,uint80)",
          "function decimals() view returns (uint8)",
        ],
        provider
      );

      const data = await feed.latestRoundData();
      const decimals = await feed.decimals();
      const bnbUsd = parseFloat(formatUnits(data[1], decimals));
      const newPrice = kindPerBNB * bnbUsd;
      setKindPrice(newPrice);
      console.log("🔄 REFRESH - KIND Price: $", newPrice.toFixed(6));
    } catch (err) {
      console.log("Price error:", err);
    }
  }, []);

  const loadPackages = useCallback(async () => {
    try {
      const count = await contract.getPackagesCount();
      let arr = [];
      let activeCount = 0;

      for (let i = 0; i < count; i++) {
        const p = await contract.packages(i);
        arr.push({
          id: i,
          name: p.name,
          price: formatUnits(p.price, 18),
          totalActive: Number(p.totalActive),
          active: p.active,
        });
        if (p.active) activeCount += Number(p.totalActive);
      }

      setPackages(arr);
      setTotalActive(activeCount);
      console.log("🔄 REFRESH - All Packages:", arr.length, "Total Active:", activeCount);
    } catch (err) {
      console.log("Packages load error:", err);
    }
  }, []);

  const loadUserPackages = useCallback(async () => {
    if (!address) {
      setMyPackages([]);
      console.log("🔄 REFRESH - No address connected");
      return;
    }

    try {
      const signer = getProvider();
      const read = new Contract(KIND_NETWORK_REWARD_CONTRACT_ADDRESS, REWARD_NETWORK_KIND_ABI, signer);
      const indexes = await getUserPackages(address);

      console.log("🔄 REFRESH - User indexes:", indexes.length);

      if (indexes.length === 0) {
        setMyPackages([]);
        console.log("🔄 REFRESH - No user packages found");
        return;
      }

      const packageGroups = {};
      for (let i of indexes) {
        const up = await read.userPackages(i);
        const pid = Number(up.packageId);
        const indexNum = Number(i);

        if (!packageGroups[pid]) {
          packageGroups[pid] = [];
        }
        packageGroups[pid].push({
          index: indexNum,
          refundable: formatUnits(up.refundable, 18),
          active: up.active,
        });
      }

      const map = {};
      for (let pidStr in packageGroups) {
        const pid = Number(pidStr);
        try {
          const stats = await read.getUserPackageStats(address, pid);
          const p = await read.packages(pid);

          map[pid] = {
            pid,
            name: p.name,
            price: formatUnits(p.price, 18),
            activeCount: Number(stats[3]),
            totalCount: Number(stats[4]),
            reward: Math.floor(Number(formatUnits(stats[0], 18))),
            refund: Number(formatUnits(stats[1], 18)),
            indexes: packageGroups[pid],
            active: Number(stats[3]) > 0,
          };
        } catch (err) {
          console.log(`PID ${pid} error:`, err);
        }
      }

      setMyPackages(Object.values(map));
      console.log("🔄 REFRESH - My Packages:", Object.values(map).length);
    } catch (err) {
      console.error("loadUser error:", err);
      setMyPackages([]);
    }
  }, [address, getProvider, getUserPackages]);

  // ✅ BACKGROUND REFRESH - NO LOADING
  const refreshAllData = useCallback(async () => {
    if (isRefreshingRef.current) return;

    isRefreshingRef.current = true;

    console.log("⏰ Time:", new Date().toLocaleTimeString());
    console.log("👤 Address:", address || "Not connected");

    try {
      await Promise.all([
        fetchKindPrice(),
        fetchKindBalance(),
        loadPackages(),
        loadUserPackages(),
      ]);
    } catch (err) {
      console.log("Refresh error:", err);
    } finally {
      isRefreshingRef.current = false;
    }
  }, [fetchKindPrice, fetchKindBalance, loadPackages, loadUserPackages, address]);

  const getUSD = (amount) => {
    if (!kindPrice || isNaN(kindPrice)) return "0.00";
    return (Number(amount) * Number(kindPrice)).toFixed(2);
  };

  // REFERRAL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const ref = params.get("ref");
    if (ref && ref !== address) localStorage.setItem("referrer", ref);
  }, [location, address]);

  // ✅ INITIAL LOAD ONLY - Shows loading spinner ONCE
// ✅ Run after user connects or when wallet is already connected
useEffect(() => {
  if (!isConnected) return;

  setLoading(true);
  refreshAllData().finally(() => {
    setLoading(false);
  });
}, [isConnected, refreshAllData]);

  // ✅ 20s BACKGROUND REFRESH - NO LOADING
  useEffect(() => {
    const interval = setInterval(() => {
      refreshAllData();
    }, 20000);

    return () => clearInterval(interval);
  }, [refreshAllData]);

  // Action handlers call refresh after success
  const buy = async (pid) => {
    const t = toast.loading("Processing...");
    try {
      setBuyLoading((prev) => ({ ...prev, [pid]: true }));
      const ref = localStorage.getItem("referrer") || ZERO;
      await buyPackage(pid, ref);
      toast.dismiss(t);
      toast.success("Purchased! 🎉");
      refreshAllData();
    } catch (err) {
      toast.dismiss(t);
      toast.error(err.message || "Buy failed ❌");
    } finally {
      setBuyLoading((prev) => ({ ...prev, [pid]: false }));
    }
  };

  const handleClaim = async (pid) => {
    const t = toast.loading("Claiming...");
    try {
      setClaimLoading((prev) => ({ ...prev, [pid]: true }));
      await claimReward(pid);
      toast.dismiss(t);
      toast.success("Claimed! 💰");
      refreshAllData();
    } catch (err) {
      toast.dismiss(t);
      toast.error(err.message || "Claim failed ❌");
    } finally {
      setClaimLoading((prev) => ({ ...prev, [pid]: false }));
    }
  };

  const handleTerminateAll = async (pid) => {
    const t = toast.loading("Terminating all shares...");
    try {
      setTerminateAllLoading((prev) => ({ ...prev, [pid]: true }));
      await terminate(pid);
      toast.dismiss(t);
      toast.success("All shares terminated! 🛑");
      refreshAllData();
    } catch (err) {
      toast.dismiss(t);
      toast.error(err.message || "Terminate failed ❌");
    } finally {
      setTerminateAllLoading((prev) => ({ ...prev, [pid]: false }));
    }
  };


  const [shareId, setShareId] = useState("");  // add this near your other useState calls


 const handleTerminateSingle = async (pid, shareId) => {
  const loadingKey = `${pid}-${shareId}`;
  const t = toast.loading("Terminating single share...");

  try {
    setTerminateSingleLoading((prev) => ({ ...prev, [loadingKey]: true }));

    await terminateShare(shareId); // pass shareId (which is the index)

    toast.dismiss(t);
    toast.success("Single share terminated! 🛑");

    refreshAllData();
    setShareId(""); // reset dropdown after success
  } catch (err) {
    toast.dismiss(t);
    toast.error(err.message || "Single terminate failed ❌");
  } finally {
    setTerminateSingleLoading((prev) => {
      const next = { ...prev };
      delete next[loadingKey]; // cleaner cleanup
      return next;
    });
  }
};

  if (!isConnected) {
  return (
    <div className="flex items-center justify-center min-h-screen bg-black px-4">
      <div className="w-full max-w-md bg-gradient-to-br from-[#0f1b2d] to-[#1a0f1f] border border-blue-500 rounded-2xl p-8 text-center">
        <div className="w-16 h-16 mx-auto mb-4 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
        <h2 className="text-2xl font-bold text-blue-400 mb-2">Connect Wallet</h2>
        <p className="text-gray-400 mb-6 text-sm">Connect to buy packages</p>
        <button
          onClick={() => open()}
          className="w-full bg-gradient-to-r from-blue-500 to-orange-500 py-3 rounded-xl font-semibold"
        >
          Connect Wallet
        </button>
      </div>
    </div>
  );
}

if (loading) {
  return (
    <div className="flex items-center justify-center h-screen bg-black">
      <div className="w-14 h-14 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
    </div>
  );
}

return (
  <div className="text-white min-h-screen bg-black p-4">
    {/* Header */}
    <div className="bg-gradient-to-r from-[#0f1b2d] via-[#0a0f1c] to-[#2a0f2f] p-6 md:p-8 rounded-b-2xl mb-6">
      <h1 className="text-2xl md:text-4xl font-bold text-blue-400 mb-3">Packages</h1>
      <div className="flex flex-wrap gap-4 text-sm text-gray-300">
        <span>{packages.length} Packages</span>
        <span>•</span>
        <span className="text-green-400">{totalActive.toLocaleString()} Active</span>
        <span>•</span>
        <span className="text-yellow-400">KIND: ${kindPrice.toFixed(6) || "Loading..."}</span>
        <span>•</span>
        <span className="text-blue-400 font-semibold">
          💰 Balance: {Number(kindBalance).toFixed(4)} KIND{" "}
          <span className="text-blue-300">(${getUSD(kindBalance)})</span>
        </span>
      </div>
    </div>

    {/* Tabs */}
    <div className="flex gap-3 mb-6 px-4">
      <button
        onClick={() => setTab("my")}
        className={`px-6 py-2 rounded-xl font-semibold transition-all ${
          tab === "my"
            ? "bg-gradient-to-r from-blue-500 to-orange-500 text-white shadow-lg"
            : "bg-gray-800 text-gray-400 hover:bg-gray-700"
        }`}
      >
        My Packages ({myPackages.length})
      </button>
      <button
        onClick={() => setTab("all")}
        className={`px-6 py-2 rounded-xl font-semibold transition-all ${
          tab === "all"
            ? "bg-gradient-to-r from-blue-500 to-orange-500 text-white shadow-lg"
            : "bg-gray-800 text-gray-400 hover:bg-gray-700"
        }`}
      >
        All Packages
      </button>
    </div>

   {/* My Packages Cards */}
{tab === "my" && (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
    {myPackages.length === 0 ? (
      <div className="col-span-full text-center py-16 text-gray-400 text-lg">
        No packages yet. Buy your first one below! 👇
      </div>
    ) : (
      myPackages.map((pkg) => (
        <div
          key={pkg.pid}
          className="bg-gradient-to-br from-[#0f1b2d] to-[#1a0f1f] border border-gray-700 p-6 rounded-2xl hover:border-blue-500 transition-all"
        >
          <h2 className="text-xl font-bold text-white mb-3">{pkg.name}</h2>
          <p className="text-gray-400 mb-4">
            {pkg.price} KIND{" "}
            <span className="text-blue-400">(${getUSD(pkg.price)})</span>
          </p>
          <div className="mb-4 p-3 bg-gray-900/50 rounded-xl">
            <p className="text-blue-400 font-semibold text-lg">
              📦 {pkg.activeCount}/{pkg.totalCount} Active Shares
            </p>
          </div>

          {/* Always show refund */}
          <div className="mb-4 flex items-center gap-2 p-3 bg-purple-500/20 border border-purple-500/50 rounded-xl">
            <p className="text-purple-400 font-bold">
              💸 Total Refund: {pkg.refund.toFixed(2)} KIND
              <span className="text-xs text-purple-300"> (${getUSD(pkg.refund)})</span>
            </p>
          </div>

          {/* Always show big reward line (even if 0) */}
          <div className="mb-4 flex items-center gap-2 p-4 bg-green-500/20 border border-green-500/50 rounded-xl">
            <p className="text-green-400 font-bold text-xl">
              💰 {pkg.reward} KIND
              <span className="text-xs text-green-300"> (${getUSD(pkg.reward)})</span>
            </p>
          </div>

          <div className="space-y-3">
            {/* Always show claim button; disabled if reward <= 0 */}
            <button
              onClick={() => {
                if (pkg.reward <= 0) return;
                handleClaim(pkg.pid);
              }}
              disabled={pkg.reward <= 0 || claimLoading[pkg.pid]}
              className="w-full bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white py-3 px-4 rounded-xl font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg"
            >
              {claimLoading[pkg.pid]
                ? "⏳ Claiming..."
                : pkg.reward > 0
                ? `💰 Claim ${pkg.reward} KIND (${getUSD(pkg.reward)})`
                : "💰 No Claim Amount"}
            </button>

            {pkg.activeCount > 0 && (
              <button
                onClick={() => handleTerminateAll(pkg.pid)}
                disabled={terminateAllLoading[pkg.pid]}
                className="w-full bg-gradient-to-r from-red-500 to-red-700 hover:from-red-600 hover:to-red-800 text-white py-3 px-4 rounded-xl font-semibold disabled:opacity-50 transition-all shadow-lg"
              >
                {terminateAllLoading[pkg.pid]
                  ? "⏳ Terminating All..."
                  : `🛑 Terminate ALL ${pkg.activeCount} Shares`}
              </button>
            )}

           {/* Single share termination - One professional button */}
{pkg.indexes && pkg.indexes.length > 1 && pkg.activeCount > 0 && (
  <div className="pt-2 border-t border-gray-700">
    <p className="text-xs text-gray-400 mb-3 text-center">
      Need to exit early? Terminate one active share.
    </p>

    <button
      onClick={() => {
        // If multiple indexes exist, pick the first active one to terminate
        const firstActive = pkg.indexes.find((share) => share.active);
        if (!firstActive) return;
        handleTerminateSingle(pkg.pid, firstActive.index);
      }}
      disabled={Object.keys(terminateSingleLoading).some(
        (key) => key.startsWith(`${pkg.pid}-`)
      )}
      className="w-full text-sm md:text-base py-3 px-4 bg-gradient-to-r from-red-500/90 to-red-700/90 hover:from-red-600/90 hover:to-red-700/90 text-white rounded-xl font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg"
    >
      {Object.keys(terminateSingleLoading).some(
        (key) => key.startsWith(`${pkg.pid}-`)
      )
        ? "⏳ Terminating Share..."
        : "🛑 Terminate One Share"}
    </button>
  </div>
)}
          </div>
        </div>
      ))
    )}
  </div>
)}

    {/* All Packages Cards */}
    {tab === "all" && (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {packages.map((pkg) => (
          <div
            key={pkg.id}
            className="bg-gradient-to-br from-[#0f1b2d] to-[#1a0f1f] border border-gray-700 p-6 rounded-2xl hover:border-blue-500 hover:shadow-xl transition-all"
          >
            <h2 className="text-xl font-bold text-white mb-2">{pkg.name}</h2>

            <p className="text-gray-400 text-sm mb-4">
              <strong>Price:</strong>{" "}
              {pkg.price} KIND{" "}
              <span className="text-blue-400">(~${getUSD(pkg.price)})</span>
            </p>

            <div className="mb-4 text-sm text-gray-400">
              <p className="mb-2">
                <strong>Start Earning Immediately From:</strong>
              </p>
              <ul className="space-y-1 list-disc list-inside">
                <li className="text-white">Daily reward pool (increases as users join)</li>
                <li className="text-white">Ongoing airdrops</li>
                <li className="text-white">6‑level referral bonuses</li>
              </ul>
            </div>

            <div className="mb-4">
              <p className="text-sm text-gray-400">
                <strong>• Active Packages:</strong>{" "}
                <span className="text-green-400 font-bold">
                  {pkg.totalActive.toLocaleString()}
                </span>{" "}
                users
              </p>
              <p className="text-xs text-purple-400 font-bold mt-1">
                Earning = ∞ (Unlimited Potential)
              </p>
            </div>

            <button
              onClick={() => buy(pkg.id)}
              disabled={buyLoading[pkg.id] || !pkg.active}
              className="w-full bg-gradient-to-r from-blue-500 to-orange-500 hover:from-blue-600 hover:to-orange-600 text-white py-3 px-4 rounded-xl font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg"
            >
              {buyLoading[pkg.id]
                ? "⏳ Processing..."
                : pkg.active
                ? "🛒 Buy Package Now"
                : "❌ Inactive"}
            </button>
          </div>
        ))}
      </div>
    )}
  </div>
);
};

export default Packages;